import React, {
    FunctionComponent,
    useEffect,
    useMemo,
    useState
} from "react";
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShareLocationIcon from "@mui/icons-material/ShareLocation";
import {
    Period,
    PeriodSelector,
    Skeleton
} from "@atiautomacao/ati-ui-library";
import {format} from "date-fns";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import TrackerPositionHeatmap, {
    TrackerPositionSeries
} from "./TrackerPositionHeatmap";

export type TrackerPositionHeatmapModalStatus =
    "idle" | "loading" | "data" | "empty" | "error";

export interface TrackerPositionHeatmapModalProps {
    open: boolean;
    onClose: () => void;
    onExited?: () => void;
    title: string;
    period: Period;
    onPeriodChange: (period: Period) => void;
    contextId: string | null;
    status: TrackerPositionHeatmapModalStatus;
    refreshing: boolean;
    series: TrackerPositionSeries[];
    isMobile: boolean;
}

const MODAL_TITLE_ID = "tracker-position-heatmap-modal-title";

const TrackerPositionHeatmapModal: FunctionComponent<
TrackerPositionHeatmapModalProps
> = ({
    open,
    onClose,
    onExited,
    title,
    period,
    onPeriodChange,
    contextId,
    status,
    refreshing,
    series,
    isMobile
}) => {
    const [hasOpened, setHasOpened] = useState(open);
    const periodKey = `${period.fromDateTime.getTime()}-${period.toDateTime.getTime()}`;
    const heatmapPeriod = useMemo(() => ({
        start: period.fromDateTime,
        end: period.toDateTime
    }), [period.fromDateTime, period.toDateTime]);

    useEffect(() => {
        if (open) {
            setHasOpened(true);
        }
    }, [open]);

    let content: JSX.Element;
    if (status === "idle" || status === "loading" || (
        status === "data" && !contextId
    )) {
        content = (
            <Skeleton
                animation="wave"
                variant="rounded"
                height="100%"
                width="100%"
            />
        );
    } else if (status === "data" && contextId) {
        content = (
            <TrackerPositionHeatmap
                contextId={contextId}
                series={series}
                period={heatmapPeriod}
                isMobile={isMobile}
                variant="expanded"
            />
        );
    } else if (status === "error") {
        content = (
            <Box className="tracker-position-heatmap-modal-state">
                <Typography>
                    Não foi possível carregar os dados.
                </Typography>
            </Box>
        );
    } else {
        content = (
            <Box className="tracker-position-heatmap-modal-state">
                <DataNotFound/>
            </Box>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby={MODAL_TITLE_ID}
            fullWidth
            maxWidth={false}
            keepMounted
            disableRestoreFocus
            TransitionProps={{onExited}}
            className="tracker-position-heatmap-modal"
            PaperProps={{
                className: "tracker-position-heatmap-modal-paper"
            }}
        >
            <Box className="tracker-position-heatmap-modal-close-row">
                <IconButton
                    onClick={onClose}
                    size="small"
                    aria-label="Fechar"
                    autoFocus
                >
                    <CloseIcon fontSize="small"/>
                </IconButton>
            </Box>
            <Box className="tracker-position-heatmap-modal-header">
                <Box className="tracker-position-heatmap-modal-title">
                    <ShareLocationIcon aria-hidden="true"/>
                    <Typography id={MODAL_TITLE_ID} variant="h6">
                        {title}
                    </Typography>
                </Box>
                <Box className="tracker-position-day-control tracker-position-day-control--modal">
                    <span className="tracker-position-day-label">Dia</span>
                    <span className="tracker-position-day-value">
                        {format(period.toDateTime, "dd/MM/yyyy")}
                    </span>
                    <PeriodSelector
                        key={periodKey}
                        styleProps={{
                            base: {
                                display: "flex",
                                alignItems: "center"
                            }
                        }}
                        mode="button"
                        hideDatetime={false}
                        hideGroup
                        disableHourSelector
                        inputFormat="dd/MM/yyyy"
                        periodInterval={{
                            day: {
                                date: period.toDateTime,
                                startTime: "04:00:00",
                                endTime: "21:00:00"
                            }
                        }}
                        period={period}
                        onChange={onPeriodChange}
                        groupByOptions={["day"]}
                    />
                </Box>
            </Box>
            <DialogContent className="tracker-position-heatmap-modal-content">
                {hasOpened && refreshing && (
                    <span
                        className="tracker-position-heatmap-live"
                        role="status"
                        aria-live="polite"
                    >
                        Atualizando dados.
                    </span>
                )}
                {hasOpened && content}
            </DialogContent>
        </Dialog>
    );
};

export default TrackerPositionHeatmapModal;
